import type { RenameCandidate } from '@cli-types/core-engine/snapshots.js'
import { RENAME_MAX_EDIT_DISTANCE } from '@cli-constants/snapshots.js'
import { levenshtein } from '@cli-utils/text.js'

/**
 * Each added entry is consumed by at most one match: without that, a single
 * `after` can be proposed as the rename target of several `before` values,
 * producing an N→1 plan that `ast-substitution.ts` then applies as overlapping
 * substitutions.
 */
export function detectRenames<T>(
  removed: T[],
  added: T[],
  name: (entry: T) => string,
  isComparable: (before: T, after: T) => boolean = () => true
): RenameCandidate[] {
  const candidates: RenameCandidate[] = []
  const consumed = new Set<number>()

  for (const before of removed) {
    const matchIndex = added.findIndex(
      (after, index) =>
        !consumed.has(index) &&
        isComparable(before, after) &&
        levenshtein(name(before), name(after)) <= RENAME_MAX_EDIT_DISTANCE
    )

    if (matchIndex !== -1) {
      consumed.add(matchIndex)
      candidates.push({ before: name(before), after: name(added[matchIndex]!) })
    }
  }

  return candidates
}
